import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClock, MapPin, Phone, X, CheckCircle2, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { logUsageEvent } from '../lib/logger';
import { Modalidade, modalidadeEmoji, isRemota } from '../lib/modalidade';
import { PosVendaOrigem } from '../../types';

/** Lead ao qual a visita será vinculada. Aceita tanto a Negociacao completa do
 *  Kanban quanto o recorte mínimo usado nas Rotas/Prospecção. */
export interface VisitaLead {
    id: number;
    titulo: string;
    cliente_nome?: string | null;
    responsavel_id?: string | null;
    /**
     * Motivo específico deste lead, usado na atribuição em lote — cada cliente tem
     * o seu ("42 dias sem contato"). Para um lead só, a prop `motivoSugestao` do
     * modal resolve; aqui é por linha.
     */
    motivoSugestao?: string | null;
}

/** Vendedor selecionável quando o gestor atribui a visita (Central de Sugestões). */
export interface VisitaResponsavelOpcao {
    id: string;
    full_name?: string | null;
    email?: string | null;
}

interface CriarVisitaModalProps {
    /** Um lead, ou vários para atribuição em lote (mesma data e mesma modalidade). */
    negociacao: VisitaLead | VisitaLead[];
    onClose: () => void;
    onCreated?: () => void;
    /** Data pré-selecionada (ex.: dia clicado na agenda). Default: hoje. */
    dataInicial?: string;
    /** Responsável da visita quando não deve herdar do lead (ex.: rota de outro vendedor). */
    responsavelId?: string | null;
    /** `tarefas.tipo`. Default 'visita'; a Central de Sugestões passa 'pos_venda'. */
    tipo?: string;
    /**
     * Lista de vendedores para o gestor escolher o responsável (spec pós-venda §5.1:
     * default = responsável atual do cliente, editável para qualquer vendedor do time).
     * Vazio/ausente = sem seletor, mantém o comportamento herdado do contexto.
     */
    responsaveis?: VisitaResponsavelOpcao[];
    /** Texto do motor de sugestão, congelado em `motivo_sugestao` (ex.: "42 dias sem contato"). */
    motivoSugestao?: string;
    /** Grava `origem_atribuicao`. Só faz sentido junto com tipo = 'pos_venda'. */
    origemAtribuicao?: PosVendaOrigem;
}

const hojeIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Sentinela do seletor de vendedor no lote: cada visita fica com o responsável
 * atual do SEU cliente. É o default do lote — os selecionados costumam ser de
 * carteiras diferentes, e forçar um único vendedor para todos seria perder o
 * padrão "default = responsável atual do cliente" da spec §5.1.
 */
const MANTER_RESPONSAVEL = '__manter__';

/**
 * Teto de espera do insert. A rede do time de campo derruba a RESPOSTA do POST
 * com a linha já commitada — sem teto o botão fica em "Salvando..." para sempre
 * (o `fetch` do navegador não desiste sozinho), o vendedor recria a visita e
 * nasce a duplicata. Ao estourar, o modal confere no banco o que de fato entrou
 * antes de acusar erro.
 */
const TIMEOUT_MS = 20_000;

/** Status em que a visita ainda está de pé — só esses contam como "já agendada". */
const STATUS_ABERTO = ['a_iniciar', 'em_andamento', 'aguardando'];

/**
 * Assinatura de uma visita já agendada: mesma dupla lead+responsável, no mesmo
 * dia e na mesma modalidade. É exatamente a assinatura das duplicatas
 * observadas em produção — dois presenciais no mesmo lead e dia, do mesmo
 * vendedor. Presencial + remota no mesmo dia continua passando, que é legítimo.
 */
type ChaveVisita = { negociacao_id?: number | null; responsavel_id?: string | null; modalidade?: string | null };
const chaveVisita = (t: ChaveVisita) => `${t.negociacao_id}|${t.responsavel_id || ''}|${t.modalidade || ''}`;

/**
 * Ponto ÚNICO de criação de visita em todo o sistema (spec Bruno 20/jul/2026 §1):
 * Kanban, Rotas, calendário e ficha do cliente abrem este mesmo modal.
 *
 * Só pede tipo + data. Solicitante, responsável e negociação vinculada são
 * herdados do contexto — nunca digitados.
 *
 * Aceita também uma LISTA de leads (atribuição em lote da Central de Sugestões):
 * mesma data e mesma modalidade para todos, num único insert.
 */
const CriarVisitaModal: React.FC<CriarVisitaModalProps> = ({
    negociacao,
    onClose,
    onCreated,
    dataInicial,
    responsavelId,
    tipo = 'visita',
    responsaveis,
    motivoSugestao,
    origemAtribuicao,
}) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [modalidade, setModalidade] = useState<Modalidade>('presencial');
    const [data, setData] = useState(dataInicial || hojeIso());
    const [salvando, setSalvando] = useState(false);

    const leads = Array.isArray(negociacao) ? negociacao : [negociacao];
    const isLote = leads.length > 1;
    const isPosVenda = tipo === 'pos_venda';
    const rotulo = isPosVenda ? 'Visita de pós-venda' : 'Visita';
    const podeEscolherResponsavel = !!responsaveis?.length;
    // Um lead: default = responsável atual dele. Lote: default = manter o de cada um.
    const [responsavelSel, setResponsavelSel] = useState<string>(
        isLote
            ? MANTER_RESPONSAVEL
            : (responsavelId || leads[0]?.responsavel_id || profile?.id || user?.id || '')
    );

    /**
     * Visitas que já existem para estes leads neste dia. Serve para dois momentos:
     * antes de gravar (a tentativa anterior pode ter commitado sem a resposta
     * voltar) e depois de um erro de rede (para saber se gravou mesmo assim).
     */
    const buscarJaAgendadas = async (signal: AbortSignal) => {
        const { data: existentes, error } = await supabase
            .from('tarefas')
            .select('negociacao_id, responsavel_id, modalidade')
            .in('negociacao_id', leads.map(l => l.id))
            .eq('vencimento', data)
            .eq('tipo', tipo)
            .in('status', STATUS_ABERTO)
            .abortSignal(signal);
        if (error) throw error;
        return new Set((existentes || []).map(chaveVisita));
    };

    const finalizar = (criadas: number) => {
        const dataFmt = new Date(data + 'T12:00:00').toLocaleDateString('pt-BR');
        if (criadas === 0) {
            // Nada pendente: a tentativa anterior tinha gravado e a resposta se
            // perdeu. Dizer "já estava agendada" evita o vendedor insistir.
            addToast(`${rotulo} já estava agendada para ${dataFmt}. Nada foi duplicado.`, 'info');
        } else {
            addToast(
                isLote
                    ? `${criadas} visitas de pós-venda criadas para ${dataFmt} ${modalidadeEmoji(modalidade)}`
                    : `${rotulo} criada na rota de ${dataFmt} ${modalidadeEmoji(modalidade)}`,
                'success'
            );
        }
        onCreated?.();
        onClose();
    };

    /** Uma linha de `tarefas` por lead — responsável e solicitante vêm do contexto. */
    const montarLinhas = () => {
        const agora = new Date().toISOString();
        const gestorId = profile?.id || user?.id;
        return leads.map(lead => {
            const forcado = podeEscolherResponsavel && responsavelSel !== MANTER_RESPONSAVEL
                ? responsavelSel
                : null;
            const responsavel = forcado
                ?? responsavelId
                ?? lead.responsavel_id
                ?? gestorId;
            return {
                titulo: `${rotulo}: ${lead.titulo}`,
                descricao: lead.cliente_nome && lead.cliente_nome !== lead.titulo ? lead.cliente_nome : null,
                responsavel_id: responsavel,
                solicitante_id: user?.id,
                prioridade: 'media',
                vencimento: data,
                status: 'a_iniciar',
                negociacao_id: lead.id,
                tipo,
                modalidade,
                // Só a visita de pós-venda carrega quem atribuiu e por quê (spec §3.1).
                origem_atribuicao: origemAtribuicao ?? null,
                atribuido_por: origemAtribuicao ? gestorId : null,
                motivo_sugestao: lead.motivoSugestao ?? motivoSugestao ?? null,
                created_at: agora,
            };
        });
    };

    const confirmar = async () => {
        if (!data || salvando || leads.length === 0) return;
        setSalvando(true);
        const linhas = montarLinhas();
        const ctrl = new AbortController();
        const timer = window.setTimeout(() => ctrl.abort(), TIMEOUT_MS);
        try {
            // Retentativa do vendedor não pode virar visita repetida: o que a
            // tentativa anterior já gravou sai da lista antes do insert.
            const jaAgendadas = await buscarJaAgendadas(ctrl.signal);
            const pendentes = linhas.filter(l => !jaAgendadas.has(chaveVisita(l)));
            if (pendentes.length > 0) {
                const { error } = await supabase.from('tarefas').insert(pendentes).abortSignal(ctrl.signal);
                if (error) throw error;
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'criar_visita', origem: 'acao' });
            }
            finalizar(pendentes.length);
        } catch (err: any) {
            // Resposta perdida não quer dizer que nada gravou. Pergunta ao banco
            // antes de acusar erro — senão a próxima tentativa duplica a visita.
            if (await gravouMesmoAssim(linhas)) {
                finalizar(linhas.length);
                return;
            }
            console.error('Erro ao criar visita:', err);
            addToast(
                ctrl.signal.aborted
                    ? 'A conexão demorou demais e a visita NÃO foi criada. Confira o sinal e tente de novo.'
                    : (isLote ? 'Erro ao atribuir as visitas.' : `Erro ao criar a ${rotulo.toLowerCase()}.`),
                'error'
            );
        } finally {
            window.clearTimeout(timer);
            setSalvando(false);
        }
    };

    /** Confere, com uma conexão nova, se as visitas entraram apesar do erro. */
    const gravouMesmoAssim = async (linhas: ChaveVisita[]) => {
        const ctrl = new AbortController();
        const timer = window.setTimeout(() => ctrl.abort(), TIMEOUT_MS);
        try {
            const jaAgendadas = await buscarJaAgendadas(ctrl.signal);
            return linhas.every(l => jaAgendadas.has(chaveVisita(l)));
        } catch {
            return false; // Sem rede para confirmar: trata como falha e avisa.
        } finally {
            window.clearTimeout(timer);
        }
    };

    const btnBase = 'flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border transition-all';
    const btnOn = 'bg-primary/10 border-primary/40 text-primary';
    const btnOff = 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300';

    return createPortal(
        // Fechar no meio da gravação não cancela o insert — só esconde o que está
        // acontecendo e leva o vendedor a reabrir e criar a visita de novo.
        <div className="fixed inset-0 z-[2100] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={() => { if (!salvando) onClose(); }}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm max-h-[90vh] overflow-y-auto flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0">
                            <CalendarClock size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                                {isLote
                                    ? `Atribuir ${leads.length} visitas de pós-venda`
                                    : (isPosVenda ? 'Atribuir visita de pós-venda' : 'Criar visita')}
                            </h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {isLote ? leads.map(l => l.titulo).join(' · ') : leads[0]?.titulo}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} disabled={salvando} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0 disabled:opacity-40">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {!isLote && motivoSugestao && (
                        <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-secondary/10 border border-secondary/30">
                            <Clock size={14} className="text-secondary shrink-0 mt-0.5" />
                            <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">{motivoSugestao}</p>
                        </div>
                    )}

                    {isLote && (
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600 max-h-40 overflow-y-auto">
                            {leads.map(l => (
                                <div key={l.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                                    <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-200 truncate">{l.titulo}</span>
                                    {l.motivoSugestao && (
                                        <span className="text-[10px] text-secondary font-bold shrink-0">{l.motivoSugestao}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {podeEscolherResponsavel && (
                        <div>
                            <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Vendedor</label>
                            <select
                                value={responsavelSel}
                                onChange={e => setResponsavelSel(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            >
                                {isLote && (
                                    <option value={MANTER_RESPONSAVEL}>Manter o responsável atual de cada cliente</option>
                                )}
                                {responsaveis!.map(r => (
                                    <option key={r.id} value={r.id}>{r.full_name || r.email || r.id}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-gray-400 mt-1">
                                {isLote
                                    ? 'Escolher um vendedor aqui sobrescreve o responsável de todos os selecionados.'
                                    : 'Começa no responsável atual do cliente — troque se a carteira mudou.'}
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Tipo da visita</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setModalidade('presencial')}
                                className={`${btnBase} ${modalidade === 'presencial' ? btnOn : btnOff}`}
                            >
                                <MapPin size={14} /> Presencial
                            </button>
                            <button
                                type="button"
                                onClick={() => setModalidade('remota')}
                                className={`${btnBase} ${isRemota(modalidade) ? btnOn : btnOff}`}
                            >
                                <Phone size={14} /> Remota
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Data da visita</label>
                        <input
                            type="date"
                            value={data}
                            min={hojeIso()}
                            onChange={e => setData(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">
                            {isRemota(modalidade)
                                ? 'Entra como "Remota do dia" — fora do cálculo de rota.'
                                : 'Entra na rota geográfica do dia escolhido.'}
                            {isLote && ` Todas as ${leads.length} caem neste mesmo dia.`}
                        </p>
                    </div>

                    <button
                        onClick={confirmar}
                        disabled={!data || salvando || (podeEscolherResponsavel && !responsavelSel)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl text-white bg-primary hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                        <CheckCircle2 size={16} />
                        {salvando
                            ? 'Salvando...'
                            : isLote ? `Atribuir ${leads.length} visitas`
                                : (isPosVenda ? 'Atribuir visita' : 'Criar visita')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CriarVisitaModal;
